import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import {
	clearAllGiftsForProduct,
	type Gift,
	selectAllGiftPackageIndex,
	selectedGiftForProductEvent,
	setAllGiftPackagesForProduct,
	setProductGiftConfig,
	useCart,
	useProductGifts
} from '@/entities/cart'
import { selectGifts } from '@/entities/cart'
import { dedupeById, isGift } from '@/entities/cart/lib/gifts-helpers'
import { selectAllGiftsByProduct } from '@/entities/cart/selectors'
import { giftsLsService } from '@/entities/cart/utils/gifts-ls-service'

import { useAppSelector } from '../../../../../store/store'

type UseGiftsWithProductHook = {
	productId: number
}

type UseGiftsWithProductResult = {
	one: Gift[]
	all: Gift[]
	selectedGift?: Gift
}

export const useGiftsWithProduct = (params: UseGiftsWithProductHook): UseGiftsWithProductResult => {
	const { productId } = params

	const dispatch = useDispatch()
	const selectedGift = useAppSelector(state => selectGifts.selectById(state as any, productId))
	const { getBasketItem } = useCart()

	const cartItem = getBasketItem(productId)
	const isProductInCart = Boolean(cartItem)

	const allGifts = useAppSelector(selectAllGiftsByProduct(productId))

	const [oneGifts, setOneGifts] = useState<Gift[]>([])

	const { checkOneProductForGifts } = useProductGifts()

	const persistRef = useRef({
		selectedGift,
		isProductInCart
	})

	const init = async () => {
		const { one = [], allPackages = [] } = await checkOneProductForGifts(productId)

		// ensure gifts are only for current product
		const validOne = (one as Gift[]).filter(isGift)
		const uniqueOne = dedupeById(validOne)
		setOneGifts(uniqueOne)

		// НОВАЯ АРХИТЕКТУРА: сохраняем изолированную конфигурацию
		if (uniqueOne.length > 0) {
			// Type: 'one' - подарки на выбор
			dispatch(
				setProductGiftConfig({
					productId,
					config: {
						type: 'one',
						selectedGift: null,
						availableOneGifts: uniqueOne,
						availablePackages: [],
						selectedPackageIndex: null,
						autoGiftsExcluded: false
					}
				})
			)
		} else {
			// normalize packages for current product
			const normalizedPackages = (allPackages || []).map(pkg => (pkg as Gift[]).filter(isGift)).map(dedupeById)

			if (normalizedPackages.length > 0) {
				// Type: 'packages' - пакеты подарков
				const autoSelectIndex = 0

				dispatch(
					setProductGiftConfig({
						productId,
						config: {
							type: 'packages',
							selectedGift: null,
							availableOneGifts: [],
							availablePackages: normalizedPackages,
							selectedPackageIndex: autoSelectIndex,
							autoGiftsExcluded: false
						}
					})
				)
			}
		}

		// СТАРАЯ ЛОГИКА (deprecated, будет удалена):
		if (uniqueOne.length > 0) {
			dispatch(clearAllGiftsForProduct({ productId }))
		} else {
			const normalizedPackages = (allPackages || []).map(pkg => (pkg as Gift[]).filter(isGift)).map(dedupeById)
			if (normalizedPackages.length === 0) {
				dispatch(clearAllGiftsForProduct({ productId }))
			} else {
				dispatch(setAllGiftPackagesForProduct({ productId, packages: normalizedPackages }))
				if (normalizedPackages.length === 1) {
					dispatch(selectAllGiftPackageIndex({ productId, index: 0 }))
				} else {
					dispatch(clearAllGiftsForProduct({ productId }))
				}
			}
		}

		// auto-select single one-gift
		if (uniqueOne.length === 1) {
			const normalizedGift: Gift = { ...uniqueOne[0], PRODUCT_IDS: [productId] }
			dispatch(selectedGiftForProductEvent({ gift: normalizedGift }))
			try {
				giftsLsService.setSingle(productId, uniqueOne[0].ID)
			} catch (_) {}
		}
	}

	useEffect(() => {
		persistRef.current = {
			selectedGift,
			isProductInCart
		}
	}, [isProductInCart, selectedGift])

	useEffect(() => {
		// reset local state to avoid leaking other product data
		setOneGifts([])
		init()

		return () => {
			const { isProductInCart } = persistRef.current
			// Keep selected one-gift; clear auto gifts only when leaving PDP and product isn't in cart
			if (!isProductInCart) {
				if (allGifts?.length) dispatch(clearAllGiftsForProduct({ productId }))
			}
		}
	}, [productId]) // eslint-disable-line react-hooks/exhaustive-deps

	return {
		one: oneGifts,
		all: allGifts,
		selectedGift
	}
}
